import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { useQueueStore } from "@/lib/store";
import { useAuthStore } from "@/lib/auth";
import { Patient, Medication, Prescription } from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Clipboard, ArrowLeft } from "lucide-react";
import { PreviousPrescriptions } from "@/components/prescriptions/PreviousPrescriptions";
import { PrescriptionForm } from "@/components/prescriptions/PrescriptionForm";
import { savePrescriptionToBackend, fetchPrescriptionsByPatient, usePrescriptionsStore } from '@/lib/stores/prescriptions';
import { supabase } from "@/lib/supabase";
import { getPatientVitals, savePatientVitals, clearPatientVitals, VitalsData } from '@/lib/services/vitalsService';

const EPrescription = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthStore();
  

  
  const queueStore = useQueueStore();
  const patients = queueStore.patients;
  const doctors = queueStore.doctors;
  const addPrescription = queueStore.addPrescription;
  const updatePrescription = queueStore.updatePrescription;
  const getPrescriptionsByPatient = queueStore.getPrescriptionsByPatient;
  const fetchDoctors = queueStore.fetchDoctors;
  
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [chiefComplaints, setChiefComplaints] = useState<string>("");
  const [complaintsDuration, setComplaintsDuration] = useState<string>("");
  const [clinicalFindings, setClinicalFindings] = useState<string>("");
  const [diagnosis, setDiagnosis] = useState<string>("");
  const [labTests, setLabTests] = useState<string>("");
  
  // List of saved medications
  const [medications, setMedications] = useState<Medication[]>([]);
  
  // Ensure medications is always an array
  useEffect(() => {
    if (!Array.isArray(medications)) {
      setMedications([]);
    }
  }, [medications]);
  
  const [existingPrescriptions, setExistingPrescriptions] = useState<Prescription[]>([]);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  
  const [followUpDays, setFollowUpDays] = useState<number>(0);
  
  // Check if there's an existing draft for this patient (computed value)
  const existingDraft = existingPrescriptions.find(p => p.status === 'draft');
  
  // Load vitals from database when patient changes
  useEffect(() => {
    const loadVitals = async () => {
      if (patientId) {
        try {
          const vitalsData = await getPatientVitals(patientId);
          if (patient) {
            setPatient({
              ...patient,
              vitals: vitalsData
            });
          }
        } catch (error) {
          console.error('Error loading vitals:', error);
        }
      }
    };
    
    loadVitals();
  }, [patientId, patient]);

  // Load existing draft data into form fields
  // Use existingPrescriptions as dependency so it re-runs when prescriptions are updated after save
  useEffect(() => {
    const existingDraft = existingPrescriptions.find(p => p.status === 'draft');
    
    if (existingDraft) {
      console.log('Loading draft data:', existingDraft);
      console.log('Draft chief_complaints:', existingDraft.chief_complaints);
      console.log('Draft lab_tests:', existingDraft.lab_tests);
      console.log('Draft lab_tests type:', typeof existingDraft.lab_tests);
      
      // Load all the saved draft data into the form fields
      // Use !== undefined && !== null && !== '' to handle empty strings
      if (existingDraft.chief_complaints !== undefined && existingDraft.chief_complaints !== null && existingDraft.chief_complaints !== '') {
        console.log('Setting chief_complaints:', existingDraft.chief_complaints);
        setChiefComplaints(existingDraft.chief_complaints);
      }
      if (existingDraft.complaints_duration !== undefined && existingDraft.complaints_duration !== null && existingDraft.complaints_duration !== '') {
        setComplaintsDuration(existingDraft.complaints_duration);
      }
      if (existingDraft.clinical_findings !== undefined && existingDraft.clinical_findings !== null && existingDraft.clinical_findings !== '') {
        setClinicalFindings(existingDraft.clinical_findings);
      }
      if (existingDraft.diagnosis !== undefined && existingDraft.diagnosis !== null && existingDraft.diagnosis !== '') {
        setDiagnosis(existingDraft.diagnosis);
      }
      if (existingDraft.notes !== undefined && existingDraft.notes !== null && existingDraft.notes !== '') {
        setNotes(existingDraft.notes);
      }
      
      // Load medications
      if (existingDraft.medications && Array.isArray(existingDraft.medications)) {
        // Ensure medications have required fields with proper types
        const typedMedications = existingDraft.medications.map(med => ({
          name: med.name || '',
          dosage: med.dosage || '',
          doseType: med.doseType || '',
          frequency: med.frequency || '',
          frequencyPattern: med.frequencyPattern || '',
          frequencyDescription: med.frequencyDescription || '',
          duration: med.duration || '',
          instructions: med.instructions || ''
        }));
        setMedications(typedMedications);
      }
      
      // Load lab tests - handle both array and string formats
      // Check for null, undefined, empty array, and empty string
      if (existingDraft.lab_tests !== undefined && existingDraft.lab_tests !== null) {
        let labTestsArray: string[] = [];
        
        if (Array.isArray(existingDraft.lab_tests)) {
          labTestsArray = existingDraft.lab_tests.filter(test => test && test.trim() !== '');
        } else if (typeof existingDraft.lab_tests === 'string') {
          // Try to parse if it's a JSON string
          try {
            const parsed = JSON.parse(existingDraft.lab_tests);
            if (Array.isArray(parsed)) {
              labTestsArray = parsed.filter(test => test && test.trim() !== '');
            } else {
              // If parsing fails or not an array, treat as comma-separated string
              labTestsArray = existingDraft.lab_tests.split(',').map(t => t.trim()).filter(Boolean);
            }
          } catch {
            // If parsing fails, treat as comma-separated string
            labTestsArray = existingDraft.lab_tests.split(',').map(t => t.trim()).filter(Boolean);
          }
        }
        
        console.log('Processed labTestsArray:', labTestsArray);
        if (labTestsArray.length > 0) {
          const labTestsString = labTestsArray.join(', ');
          console.log('Setting lab tests:', labTestsString);
          setLabTests(labTestsString);
        } else {
          console.log('Lab tests array is empty, clearing field');
          setLabTests('');
        }
      } else {
        console.log('Lab tests is null/undefined, clearing field');
        setLabTests('');
      }
      
      // Load follow-up date
      if (existingDraft.follow_up_date) {
        const followUpDate = new Date(existingDraft.follow_up_date);
        const today = new Date();
        const diffTime = followUpDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          setFollowUpDays(diffDays);
        }
      }
    } else {
      console.log('No draft found in existingPrescriptions');
    }
  }, [existingPrescriptions]); // Changed dependency to existingPrescriptions so it re-runs when prescriptions are updated

  // Handle editing a draft prescription
  const handleEditDraft = (prescription: Prescription) => {
    console.log('Loading draft for editing:', prescription);
    
    // Load the draft data into the form
    if (prescription.chief_complaints) {
      setChiefComplaints(prescription.chief_complaints);
    }
    if (prescription.complaints_duration) {
      setComplaintsDuration(prescription.complaints_duration);
    }
    if (prescription.clinical_findings) {
      setClinicalFindings(prescription.clinical_findings);
    }
    if (prescription.diagnosis) {
      setDiagnosis(prescription.diagnosis);
    }
    if (prescription.notes) {
      setNotes(prescription.notes);
    }
    
    // Load medications from the draft
    if (prescription.medications && Array.isArray(prescription.medications)) {
      const typedMedications = prescription.medications.map(med => ({
        name: med.name || '',
        dosage: med.dosage || '',
        doseType: med.doseType || '',
        frequency: med.frequency || '',
        frequencyPattern: med.frequencyPattern || '',
        frequencyDescription: med.frequencyDescription || '',
        duration: med.duration || '',
        instructions: med.instructions || ''
      }));
      setMedications(typedMedications);
    }
    
    // Load lab tests - handle both array and string formats
    if (prescription.lab_tests) {
      let labTestsArray: string[] = [];
      
      if (Array.isArray(prescription.lab_tests)) {
        labTestsArray = prescription.lab_tests;
      } else if (typeof prescription.lab_tests === 'string') {
        // Try to parse if it's a JSON string
        try {
          const parsed = JSON.parse(prescription.lab_tests);
          labTestsArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          // If parsing fails, treat as comma-separated string
          labTestsArray = prescription.lab_tests.split(',').map(t => t.trim()).filter(Boolean);
        }
      }
      
      if (labTestsArray.length > 0) {
        setLabTests(labTestsArray.join(', '));
      } else {
        setLabTests(''); // Clear if empty
      }
    } else {
      setLabTests(''); // Clear if not present
    }
    
    if (prescription.follow_up_date) {
      const followUpDate = new Date(prescription.follow_up_date);
      const today = new Date();
      const diffTime = followUpDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        setFollowUpDays(diffDays);
      }
    }
    
    toast({
      title: "Draft loaded",
      description: "The draft prescription has been loaded for editing"
    });
  };
  
  // Get patient details
  useEffect(() => {
    if (patientId) {
      const foundPatient = patients.find(p => p.id === patientId);
      if (foundPatient) {
        setPatient(foundPatient);
        // If doctor is logged in, use their ID
        if (user?.id) {
          setDoctor(user.id);
        } else if (foundPatient.doctorId) {
          setDoctor(foundPatient.doctorId);
        }
        // Fetch prescriptions from Supabase
        fetchPrescriptionsByPatient(patientId).then((prescriptions) => {
          setExistingPrescriptions(prescriptions);
        });
      }
    }
  }, [patientId, patients, user]);
  
  // Ensure doctors are loaded
  useEffect(() => {
    if (doctors.length === 0) {
      fetchDoctors();
    }
  }, [doctors, fetchDoctors]);


  
  const handleAddMedication = (medication: Medication) => {
    setMedications(prevMedications => [...prevMedications, medication]);
  };

  const handleAddMultipleMedications = (newMedications: Medication[]) => {
    setMedications(prevMedications => [...prevMedications, ...newMedications]);
  };
  
  const handleRemoveMedication = (index: number) => {
    const updatedMedications = [...medications];
    updatedMedications.splice(index, 1);
    setMedications(updatedMedications);
  };

  const handleUpdateMedication = (medication: Medication, index: number) => {
    const updatedMedications = [...medications];
    updatedMedications[index] = medication;
    setMedications(updatedMedications);
  };


  
  const handleSavePrescription = async (status: "draft" | "issued" = "draft") => {

    if (!patientId || !doctor) {
      console.error('Missing required information:', { patientId, doctor });
      toast({
        title: "Missing information",
        description: "Patient and doctor information is required",
        variant: "destructive"
      });
      return;
    }
    // Only require medications for issued prescriptions, not for drafts
    if (status === "issued" && medications.length === 0) {
      console.error('No medications provided for issued prescription');
      toast({
        title: "Missing medication details",
        description: "Please add at least one medication to create a prescription",
        variant: "destructive"
      });
      return;
    }
    try {
      // Calculate follow-up date if days are specified
      let followUpDate = null;
      if (followUpDays > 0) {
        const followUp = new Date();
        followUp.setDate(followUp.getDate() + followUpDays);
        followUpDate = followUp.toISOString();
      }


      // Save prescription to Supabase
      const result = await savePrescriptionToBackend({
        patientId,
        doctorId: doctor,
        medications: medications || [], // Ensure medications is always an array
        instructions: 'Draft prescription - instructions to be added', // Provide a default instruction for drafts
        notes: notes,
        chief_complaints: chiefComplaints,
        complaints_duration: complaintsDuration,
        clinical_findings: clinicalFindings,
        diagnosis: diagnosis,
        lab_tests: labTests ? labTests.split(', ').filter(Boolean) : [],
        follow_up_date: followUpDate,
        status: status,
        vitals: patient?.vitals, // Include vitals with prescription
        updatedAt: new Date().toISOString()
      });

      // If prescription is issued and has follow-up date, update patient's follow-up date
      if (status === "issued" && followUpDate && patient) {
        try {
          await queueStore.updatePatientFollowUp(patientId, new Date(followUpDate), notes);
        } catch (error) {
          console.error('Error updating patient follow-up date:', error);
          // Don't fail the prescription save if follow-up update fails
        }
      }

      // Clear vitals after prescription is issued (visit completed)
      if (status === "issued" && patient) {
        try {
          await clearPatientVitals(patientId);
          setPatient({
            ...patient,
            vitals: undefined
          });
        } catch (error) {
          console.error('Error clearing vitals:', error);
          // Don't fail the prescription save if vitals clearing fails
        }
      }

      // Refetch prescriptions from Supabase
      const prescriptions = await fetchPrescriptionsByPatient(patientId);
      setExistingPrescriptions(prescriptions);
      
      // Update the Zustand store with fresh prescription data
      await usePrescriptionsStore.getState().fetchPrescriptions();
      
      // Check if this was an update to existing draft or new draft
      const isDraftUpdate = existingPrescriptions.some(p => p.status === 'draft');
      
      
      toast({
        title: status === "issued" ? "Prescription created" : (isDraftUpdate ? "Draft updated" : "Draft created"),
        description: status === "issued" 
          ? followUpDate 
            ? `The prescription has been created successfully. Follow-up scheduled for ${new Date(followUpDate).toLocaleDateString()}`
            : "The prescription has been created successfully"
          : isDraftUpdate 
            ? "Your existing draft has been updated with the latest changes."
            : "Your draft has been created successfully. You can continue editing later."
      });
      
      // For drafts, stay on the same page to continue editing
      // For issued prescriptions, clear form and navigate to patient page
      if (status === "issued") {
        
        // Clear all form fields after successful issuance
        setChiefComplaints("");
        setComplaintsDuration("");
        setClinicalFindings("");
        setDiagnosis("");
        setLabTests("");
        setNotes("");
        setMedications([]);
        setFollowUpDays(0);
        
        // Navigate to patient page
        navigate(`/patient/${patientId}`);
      } else {
        // For drafts, show a brief success indicator and keep form data
        // The form will remain filled so users can continue editing
      }
    } catch (error) {
      console.error('Error saving prescription:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save prescription. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const getDoctorName = (doctorId: string) => {
    const foundDoctor = doctors.find(d => d.id === doctorId);
    return foundDoctor ? foundDoctor.name : "Unknown Doctor";
  };
  
  if (!patient) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <p>Loading patient information...</p>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="space-y-6">
        

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => navigate('/doctor-dashboard')}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/patient/${patientId}`)}
                className="w-full sm:w-auto"
              >
                Back to Patient
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Clipboard className="h-6 w-6 text-hospital-primary" />
              <h1 className="text-2xl font-bold">E-Prescription</h1>
              {existingDraft && (
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  Draft in progress
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <PrescriptionForm
              patient={patient}
              doctorName={getDoctorName(doctor)}
              chiefComplaints={chiefComplaints}
              setChiefComplaints={setChiefComplaints}
              complaintsDuration={complaintsDuration}
              setComplaintsDuration={setComplaintsDuration}
              clinicalFindings={clinicalFindings}
              setClinicalFindings={setClinicalFindings}
              diagnosis={diagnosis}
              setDiagnosis={setDiagnosis}
              labTests={labTests}
              setLabTests={setLabTests}
              medications={medications}
              handleAddMedication={handleAddMedication}
              handleAddMultipleMedications={handleAddMultipleMedications}
              handleRemoveMedication={handleRemoveMedication}
              handleUpdateMedication={handleUpdateMedication}
              notes={notes}
              setNotes={setNotes}
              followUpDays={followUpDays}
              setFollowUpDays={setFollowUpDays}
              handleSavePrescription={handleSavePrescription}
              existingDraft={!!existingDraft}
            />
          </div>
          
          <div className="space-y-6">
            <PreviousPrescriptions
              prescriptions={existingPrescriptions}
              patientId={patientId || ""}
              getDoctorName={getDoctorName}
              doctors={doctors}
              onEditDraft={handleEditDraft}
            />
            
            {/* Vitals Block */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vitals</CardTitle>
                <CardDescription>
                  {patient?.vitals ? 'Patient vitals (editable by doctor)' : 'No vitals recorded yet - Empty fields will appear with dashed borders'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bp-systolic">BP (Systolic)</Label>
                    <Input
                      id="bp-systolic"
                      type="number"
                      value={patient?.vitals?.bpSystolic || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            bpSystolic: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter systolic BP"
                      className={`text-sm ${!patient?.vitals?.bpSystolic ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bp-diastolic">BP (Diastolic)</Label>
                    <Input
                      id="bp-diastolic"
                      type="number"
                      value={patient?.vitals?.bpDiastolic || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            bpDiastolic: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter diastolic BP"
                      className={`text-sm ${!patient?.vitals?.bpDiastolic ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="temperature">Temperature (°F)</Label>
                    <Input
                      id="temperature"
                      type="number"
                      step="0.1"
                      value={patient?.vitals?.temperature || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            temperature: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter temperature (°F)"
                      className={`text-sm ${!patient?.vitals?.temperature ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pulse">Pulse (BPM)</Label>
                    <Input
                      id="pulse"
                      type="number"
                      value={patient?.vitals?.pulse || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            pulse: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter pulse (BPM)"
                      className={`text-sm ${!patient?.vitals?.pulse ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="respiratory-rate">Respiratory Rate</Label>
                    <Input
                      id="respiratory-rate"
                      type="number"
                      value={patient?.vitals?.respiratoryRate || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            respiratoryRate: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter respiratory rate"
                      className={`text-sm ${!patient?.vitals?.respiratoryRate ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oxygen-saturation">SpO2 (%)</Label>
                    <Input
                      id="oxygen-saturation"
                      type="number"
                      value={patient?.vitals?.oxygenSaturation || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const newVitals = {
                            ...patient.vitals,
                            oxygenSaturation: e.target.value,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter SpO2 (%)"
                      className={`text-sm ${!patient?.vitals?.oxygenSaturation ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      value={patient?.vitals?.weight || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const weight = e.target.value;
                          const height = patient.vitals?.height || '';
                          let bmi = patient.vitals?.bmi || '';
                          
                          // Auto-calculate BMI
                          if (weight && height) {
                            bmi = (parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2)).toFixed(1);
                          }
                          
                          const newVitals = {
                            ...patient.vitals,
                            weight,
                            bmi,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter weight (kg)"
                      className={`text-sm ${!patient?.vitals?.weight ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="height">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={patient?.vitals?.height || ''}
                      onChange={async (e) => {
                        if (patient) {
                          const height = e.target.value;
                          const weight = patient.vitals?.weight || '';
                          let bmi = patient.vitals?.bmi || '';
                          
                          // Auto-calculate BMI
                          if (height && weight) {
                            bmi = (parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2)).toFixed(1);
                          }
                          
                          const newVitals = {
                            ...patient.vitals,
                            height,
                            bmi,
                            recordedAt: new Date().toISOString(),
                            recordedBy: user?.id || 'unknown'
                          };
                          
                          setPatient({
                            ...patient,
                            vitals: newVitals
                          });
                          
                          try {
                            await savePatientVitals(patientId!, newVitals);
                          } catch (error) {
                            console.error('Error saving vitals:', error);
                          }
                        }
                      }}
                      placeholder="Enter height (cm)"
                      className={`text-sm ${!patient?.vitals?.height ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bmi">BMI</Label>
                    <Input
                      id="bmi"
                      type="number"
                      step="0.1"
                      value={patient?.vitals?.bmi || ''}
                      placeholder="Auto-calculated"
                      className={`text-sm ${!patient?.vitals?.bmi ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                      readOnly
                    />
                  </div>
                </div>
                
                {patient?.vitals?.recordedAt && (
                  <div className="text-xs text-muted-foreground pt-2 border-t">
                    Last updated: {new Date(patient.vitals.recordedAt).toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>
            
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EPrescription;
